import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import get from 'lodash/get'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { useFirmReportSummary } from '../../../api/coreData'
import Icon from '../../atoms/Icon'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import { TEXT_ALIGNMENTS } from '../../../constants'
import { useDenaliFilter } from './filters/DenaliFilterContextProvider'

const useStyles = makeStyles((theme) => ({
  daMetric: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.layout.gap.tight,
    padding: '10px',
    '& .__title': {
      fontSize: theme.typography.fontSizes.xl
    },
    '& .__metric': {
      fontSize: theme.typography.fontSizes.h1
    },
    '& .__left': {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${theme.layout.padding.none} ${theme.layout.padding.thick}`,
      fill: 'var(--color-primary-500)'
    },
    '& .__right': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.layout.gap.tight
    }
  }
}))

const useMultipleFormats = (format) => {
  const formats = useMemo(() => [format].flat(), [format])
  const [currentFormat, setCurrentFormat] = useState(formats?.[0] ?? '')
  const hasMultipleFormats = useMemo(() => formats.length > 1, [formats])

  const incrementFormat = useCallback(() => {
    if (formats.length <= 1) return
    const currentIndex = formats.indexOf(currentFormat)
    const nextIndex = (currentIndex + 1) % formats.length
    setCurrentFormat(formats[nextIndex])
  }, [currentFormat, formats])

  return {
    hasMultipleFormats,
    currentFormat,
    incrementFormat
  }
}

function Metric ({ reportId, format, accessor, title, className, style, children, icon, align }) {
  const classes = useStyles()
  const { formatter } = useFormattingContext()
  const { membershipFilter, filter } = useDenaliFilter()
  const { data, isLoading } = useFirmReportSummary(reportId, { membershipFilter, filters: filter })

  const { currentFormat, hasMultipleFormats, incrementFormat } = useMultipleFormats(format)

  const value = useMemo(() => {
    if (!data?.summary?.length) return null
    const dv = data.summary.reduce((p, c) => {
      p[c.key] = c
      return p
    }, {})
    return get(dv, accessor)
  }, [data, accessor])

  return (
    <div
      className={clsx(className, classes.daMetric)}
      style={style}
      {...(hasMultipleFormats && {
        onClick: incrementFormat,
        role: 'button',
        tabIndex: 0,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            incrementFormat()
          }
        }
      })}
    >
      {icon ? (
        <div className='__left'>
          <Icon name={icon} customSize='50px' />
        </div>
      ) : null}
      <div className='__right'>
        <div className={clsx('__title', {
          'text-left': align === TEXT_ALIGNMENTS.left,
          'text-center': align === TEXT_ALIGNMENTS.center,
          'text-right': align === TEXT_ALIGNMENTS.right
        })}
        >{title}
        </div>
        {isLoading ? (
          <CircularProgress size={32} />
        ) : (
          <div className={clsx('__metric', {
            'text-left': align === TEXT_ALIGNMENTS.left,
            'text-center': align === TEXT_ALIGNMENTS.center,
            'text-right': align === TEXT_ALIGNMENTS.right
          })}
          >{formatter(value, currentFormat)}
          </div>
        )}
        {children}
      </div>
    </div>
  )
}

Metric.propTypes = {
  reportId: PropTypes.string,
  accessor: PropTypes.string,
  title: PropTypes.string,
  format: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node,
  icon: PropTypes.string,
  align: PropTypes.oneOf(Object.values(TEXT_ALIGNMENTS))
}

Metric.defaultProps = {
  align: TEXT_ALIGNMENTS.left
}

export default Metric
